import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Phone, MapPin, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import api, { fmt, fmtDate } from "@/lib/api";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: c, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => (await api.get(`/customers/${id}`)).data,
  });

  if (isLoading || !c) return <div className="h-64 rounded-xl bg-muted animate-pulse" />;

  const sendReminder = () => {
    let digits = (c.phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("Customer has no phone number"); return; }
    if (digits.length === 10) digits = "91" + digits;
    const pendingOrders = c.orders.filter((o) => o.remaining > 0 && o.status !== "Cancelled");
    const msg = [
      `*ANAMTA NOOR*`,
      `_Zari Handwork · Handcrafted Elegance_`,
      ``,
      `Dear *${c.name}*,`,
      `A gentle reminder regarding your pending balance:`,
      ``,
      `*Pending Orders:*`,
      ...pendingOrders.map((o, i) => `  ${i + 1}. Order ${o.order_number} — ${fmt(o.remaining)} pending`),
      ``,
      `*Total Pending: ${fmt(c.pending_amount)}*`,
      ``,
      `Kindly clear the balance at your convenience.`,
      ``,
      `Thank you for choosing us!`,
      `— Anamta Noor`,
    ].join("\n");
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-button" onClick={() => navigate("/customers")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{c.name}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-0.5">
            {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
            {c.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.address}</span>}
          </div>
        </div>
        {c.pending_amount > 0 && (
          <Button data-testid="customer-reminder-button" variant="outline" className="rounded-full text-amber-500 hover:text-amber-600 active:scale-95 transition-transform" onClick={sendReminder}>
            <BellRing className="h-4 w-4 mr-1.5" /> Remind on WhatsApp
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Amount", value: fmt(c.total_amount) },
          { label: "Paid / Advance", value: fmt(c.paid_amount), cls: "text-green-600" },
          { label: "Remaining", value: fmt(c.pending_amount), cls: c.pending_amount > 0 ? "text-red-500" : "" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border bg-card p-5">
            <p className={`font-heading text-xl lg:text-2xl font-semibold ${s.cls || ""}`}>{s.value}</p>
            <p className="text-xs lg:text-sm text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-card">
        <h2 className="font-heading font-semibold px-6 py-4 border-b">Orders ({c.orders.length})</h2>
        {c.orders.length === 0 ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Order #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {c.orders.map((o) => (
                <TableRow key={o.id} data-testid={`customer-order-row-${o.id}`} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/orders/${o.id}`)}>
                  <TableCell className="font-medium">{o.order_number}</TableCell>
                  <TableCell>{fmtDate(o.order_date)}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-right">{fmt(o.total_amount)}</TableCell>
                  <TableCell className="text-right">{fmt(o.remaining)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-xl border bg-card">
        <h2 className="font-heading font-semibold px-6 py-4 border-b">Payment History</h2>
        {c.payments.length === 0 ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">No payments recorded.</p>
        ) : (
          <ul className="divide-y">
            {c.payments.map((p) => (
              <li key={p.id} className="px-6 py-3 flex items-center justify-between text-sm">
                <span>{fmtDate(p.date)} {p.remarks && <span className="text-muted-foreground">· {p.remarks}</span>}</span>
                <span className="font-medium text-green-600">{fmt(p.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
